<script lang="ts">
	import { Image } from "@unpic/svelte";
	import { Heading, Hr } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import type { LandingPage } from "#gen/bundles/v1/bundle_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import TokenizedAvatar from "#lib/components/TokenizedAvatar.svelte";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import type { LayoutProps } from "#lib/components/flow/viewer/layout/types.js";
	import { APP_ANCHOR_NAME, CONVERSION_ANCHOR_NAME, SECTION_1_ANCHOR_NAME, SECTION_2_ANCHOR_NAME } from "#lib/dag.js";
	import { transformURL } from "#lib/image.js";
	import { getHeaderBackgroundImage } from "#lib/theme.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const { container: landingPage, mode, style, share, section1, section2, conversion, apps, accessory, footer }: LayoutProps<LandingPage> = $props();
	const headerBackgroundImage = $derived(transformURL(getHeaderBackgroundImage(landingPage)));
</script>

<div class="flex h-full w-full flex-col justify-start text-[--header-color]" {style}>
	<div class="relative h-48 w-full flex-none" style="color: var(--header-color); background-color: var(--header-background-color);">
		{#if headerBackgroundImage}
			<div class="absolute inset-0">
				<Image src={headerBackgroundImage} height={192} width={480} layout="constrained" priority={true} style="height: 100%;" />
			</div>
		{/if}
		<div class="relative pt-4">
			{@render share?.()}
		</div>
	</div>
	<div class="relative mt-3 flex grow flex-col items-center gap-6">
		<div class="absolute -top-12 right-0 left-0 h-16 rounded-t-[40px] bg-white"></div>
		<div class="absolute -top-24">
			{#if landingPage.images.length > 0}
				<TokenizedAvatar provider={$tokenProvider} priority={true} size="xxl" src={landingPage.images[0].url} alt={landingPage.images[0].altText} avatarClass="border border-white" {mode} />
			{:else}
				<TokenizedAvatar provider={$tokenProvider} size="xxl" avatarClass="border border-white" {mode} />
			{/if}
		</div>
		<div class="flex w-full grow flex-col items-center gap-2 bg-white px-4 pt-28 pb-4">
			<div class="relative flex-none justify-center">
				{@render section1?.()}
				{@render accessory?.[SECTION_1_ANCHOR_NAME]?.()}
			</div>
			<div class="flex flex-none flex-col gap-1">
				<Heading tag="h1" style="color: var(--header-color);" class="text-center text-2xl leading-tight font-semibold tracking-tight">
					<TokenizedText provider={$tokenProvider} text={landingPage.primaryText} {mode} />
				</Heading>
				<div class="flex flex-col items-center gap-1">
					{#if landingPage.secondaryText}
						<P size="base" color="" align="center" class="ellipsis flex items-center gap-1 pt-1">
							{#if landingPage.secondaryText.icon}
								<Icon iconSet={landingPage.secondaryText.iconSet} name={landingPage.secondaryText.icon} />
							{/if}
							<TokenizedText provider={$tokenProvider} text={landingPage.secondaryText.text} {mode} />
						</P>
					{/if}
					{#each landingPage.tertiaryText as item, i (i)}
						<P size="base" color="" align="center" class="ellipsis flex items-center gap-1 pt-1 opacity-50">
							{#if item.icon}
								<Icon iconSet={item.iconSet} name={item.icon} />
							{/if}
							<TokenizedText provider={$tokenProvider} text={item.text} {mode} />
						</P>
					{/each}
				</div>
			</div>
			<div class="flex w-full grow flex-col gap-4 pt-2">
				{#if landingPage.supportingText}
					<Hr class="m-0" />
					<P class="ellipsis w-full justify-center text-center font-medium whitespace-pre-wrap">
						<TokenizedText provider={$tokenProvider} text={landingPage.supportingText} {mode} />
					</P>
				{/if}
				<div class="relative">
					{@render conversion?.()}
					{@render accessory?.[CONVERSION_ANCHOR_NAME]?.()}
				</div>
				<div class="relative">
					{@render section2?.()}
					{@render accessory?.[SECTION_2_ANCHOR_NAME]?.()}
				</div>
				<div class="relative h-full">
					<div class="flex flex-col gap-3">
						<div class="relative">
							{@render apps?.()}
						</div>
						{#if accessory?.[APP_ANCHOR_NAME]}
							<div class="relative">
								{@render accessory?.[APP_ANCHOR_NAME]?.()}
							</div>
						{/if}
					</div>
				</div>
			</div>
		</div>
	</div>
	{@render footer?.(mode)}
</div>
